import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import rehypeRaw from 'rehype-raw';
import MermaidRenderer from './MermaidRenderer';
import EChartsRenderer from './EChartsRenderer';
import DataGridRenderer from './DataGridRenderer';
import KLineRenderer from './KLineRenderer';
import TopologyRenderer from './TopologyRenderer';
import WorkflowRenderer from './WorkflowRenderer';
import TimelineRenderer from './TimelineRenderer';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { SpreadsheetBlock, A4PaperBlock } from './ChatBlockRenderers';
import JsonRedlineRenderer from './JsonRedlineRenderer';
import type { WordDocumentJson } from './JsonRedlineRenderer';
import JsonExcelRenderer from './JsonExcelRenderer';
import type { ExcelDocumentJson } from './JsonExcelRenderer';
import JsonPptRenderer from './JsonPptRenderer';
import type { PptDocumentJson } from './JsonPptRenderer';
import {
  ExternalLink,
  BookOpen,
  Scale,
  FileText,
  Gavel,
  ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Hash,
  File as FileIcon,
  FileImage,
  Brain,
  Cloud,
  Mail,
  HardDrive,
  FolderOpen
} from 'lucide-react';
import { Message } from '../types';
import { DocumentDrawer } from './DocumentDrawer';
import { CitationTag, CitationSource } from './CitationTag';
import ThinkingProcess from './ThinkingProcess';
import AIThinkingIndicator from './AIThinkingIndicator';
import { useTranslation } from '../hooks/useTranslation';

interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
  currentLanguage?: string;
  previousUserContent?: string; // 上一条用户消息内容，用于判断是否简单对话
}

// ==================== 引用类型定义 ====================

type CitationType = 'case' | 'statute' | 'regulation' | 'secondary' | 'generic';
type VerificationStatus = 'verified' | 'unverified' | 'warning';

interface CitationStyle {
  Icon: React.ElementType;
  bgClass: string;
  textClass: string;
  borderClass: string;
  hoverClass: string;
}

// ==================== 引用样式配置 (Based on design doc) ====================

const CITATION_STYLES: Record<CitationType, CitationStyle> = {
  // 联邦/高等法院案例 - 深蓝色 (High Authority)
  case: {
    Icon: Scale,
    bgClass: 'bg-blue-50',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-200',
    hoverClass: 'hover:bg-blue-100 hover:text-blue-800',
  },
  // 法规法典 - 青色 (Legislative Authority)
  statute: {
    Icon: BookOpen,
    bgClass: 'bg-teal-50',
    textClass: 'text-teal-700',
    borderClass: 'border-teal-200',
    hoverClass: 'hover:bg-teal-100 hover:text-teal-800',
  },
  // 条例/行政法规 - 绿色
  regulation: {
    Icon: Gavel,
    bgClass: 'bg-green-50',
    textClass: 'text-green-700',
    borderClass: 'border-green-200',
    hoverClass: 'hover:bg-green-100 hover:text-green-800',
  },
  // 二级来源 - 琥珀色
  secondary: {
    Icon: FileText,
    bgClass: 'bg-amber-50',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-200',
    hoverClass: 'hover:bg-amber-100 hover:text-amber-800',
  },
  // 通用外部链接 - 灰色
  generic: {
    Icon: ExternalLink,
    bgClass: 'bg-gray-50',
    textClass: 'text-gray-700',
    borderClass: 'border-gray-200',
    hoverClass: 'hover:bg-gray-100 hover:text-gray-800',
  },
};

// 验证状态样式
const VERIFICATION_STYLES: Record<VerificationStatus, { dotClass: string; borderStyle: string }> = {
  verified: { dotClass: 'bg-green-500', borderStyle: 'border-solid' },
  unverified: { dotClass: 'bg-gray-400', borderStyle: 'border-dashed' },
  warning: { dotClass: 'bg-red-500', borderStyle: 'border-dashed' },
};

// ==================== Content Page URL Detection ====================
// Uses POSITIVE matching only - URL must contain identifiable content markers to be a citation

/**
 * Check if a URL is a content page (actual legal document) vs a search/index page
 * Uses POSITIVE matching: URL must contain specific content identifiers to be cited
 * Examples of content identifiers:
 * - Law numbers: 729/2018, 20180729
 * - ELI identifiers: /eli/
 * - Case references: KKO:2020:15, C-123/45
 * - Document IDs in path: /doc/12345, /article/123
 * - File extensions: .pdf, .html with ID
 */
const isContentPageUrl = (href: string): boolean => {
  if (!href) return false;

  try {
    const url = new URL(href);
    const pathname = url.pathname;
    const fullUrl = href.toLowerCase();

    // ============ POSITIVE CONTENT IDENTIFIERS ============
    // URL must match at least one of these patterns to be considered a content page

    const contentIdentifierPatterns = [
      // === Legal Document Number Patterns ===
      /\/\d{4}\/\d{6,}/,                    // Year + 6+ digit ID: /2018/20180729
      /\/\d{4}\/\d{3,5}/,                   // Year + 3-5 digit ID: /2018/729
      /\/\d{6,}/,                           // 6+ digit document ID: /20180729
      /\d{1,4}\/\d{4}/,                     // Law number format: 729/2018

      // === ELI and URN Identifiers (International Standards) ===
      /\/eli\//i,                           // European Legislation Identifier
      /\/urn:/i,                            // Uniform Resource Name
      /\/celex\//i,                         // EUR-Lex CELEX number

      // === Court Case Identifiers ===
      /kko:\d{4}:\d+/i,                     // Finnish Supreme Court: KKO:2020:15
      /hao:\d{4}:\d+/i,                     // Finnish Administrative Court
      /[a-z]+-\d+\/\d+/i,                   // EU Case format: C-123/45, T-456/78
      /\d+\s*u\.?s\.?\s*\d+/i,              // US Supreme Court: 410 U.S. 113

      // === Specific Document Path Patterns ===
      /\/laki\/ajantasa\//,                 // Finlex current law
      /\/laki\/alkup\//,                    // Finlex original law
      /\/oikeus\/[a-z]+\/[a-z]+\/\d/,       // Finlex court decision with ID
      /\/legal-content\//,                  // EUR-Lex legal content
      /\/diario_boe\/txt/,                  // BOE text content
      /\/doc\.php\?/,                       // Document PHP with params
      /\/txt\.php\?/,                       // Text PHP with params

      // === Document Files ===
      /\.pdf(\?|$)/i,                       // PDF files
      /\.docx?(\?|$)/i,                     // Word documents

      // === Article/Act/Section Identifiers ===
      /\/article\/\d+/i,                    // Article with number
      /\/act\/\d+/i,                        // Act with number
      /\/section\/\d+/i,                    // Section with number
      /\/regulation\/\d+/i,                 // Regulation with number
      /\/decision\/\d+/i,                   // Decision with number
      /\/judgment\/\d+/i,                   // Judgment with number
      /\/statute\/\d+/i,                    // Statute with number

      // === ID Parameters in Query String ===
      /[?&](id|docid|doc_id|ref)=\d+/i,     // ID in query params
      /[?&]celex=/i,                        // CELEX in query params
    ];

    // Check if URL matches any content identifier pattern
    for (const pattern of contentIdentifierPatterns) {
      if (pattern.test(pathname) || pattern.test(fullUrl)) {
        return true;
      }
    }

    // ============ FALLBACK: Deep Path Heuristic ============
    // If no specific pattern matched, check path depth
    // Paths with 4+ segments likely contain specific content
    const pathSegments = pathname.split('/').filter(s => s.length > 0);
    if (pathSegments.length >= 4) {
      // Additional check: at least one segment should contain a number (document ID)
      const hasNumericSegment = pathSegments.some(seg => /\d{3,}/.test(seg));
      if (hasNumericSegment) {
        return true;
      }
    }

    // Default: NOT a content page (likely search/index page)
    return false;
  } catch {
    // If URL parsing fails, be conservative - not a content page
    return false;
  }
};

// ==================== 引用类型检测 ====================

const detectCitationType = (href: string, text: string): CitationType => {
  const lowerText = text.toLowerCase();
  const lowerHref = href.toLowerCase();

  // 案例 - 法院判决
  if (/\b(sts|ts|audiencia|tribunal|court|case|判决|案例|裁判|kko|hao|supreme|appeal)\b/i.test(text) ||
    lowerHref.includes('jurisprudencia') ||
    lowerHref.includes('oikeus') ||
    lowerHref.includes('caselaw') ||
    (lowerHref.includes('search') && lowerHref.includes('judicial')) ||
    /\d+\s*U\.?S\.?\s*\d+/.test(text) ||  // US Citation: 410 U.S. 113
    /C-\d+\/\d+/.test(text)) {             // EU Case: C-123/45
    return 'case';
  }

  // 法规法典 - 法律条文
  if (/《[^》]+》.*第.*条/.test(text) ||
    /article|section|§|条款|pykälä|laki|law|act|code/i.test(text) ||
    lowerHref.includes('finlex.fi/fi/laki') ||
    lowerHref.includes('boe.es') ||
    lowerHref.includes('legislation') ||
    lowerHref.includes('uscode') ||
    lowerHref.includes('legifrance')) {
    return 'statute';
  }

  // 条例/行政法规
  if (/条例|法规|标准|规则|regulation|directive|standard|规范|asetus|määräys/i.test(text) ||
    lowerHref.includes('eur-lex')) {
    return 'regulation';
  }

  // 法律文书/二级来源
  if (/《[^》]+法[^》]*》/.test(text) ||
    /commentary|guide|handbook|manual|指南|手册/i.test(text)) {
    return 'secondary';
  }

  return 'generic';
};

// 模拟验证状态（实际项目中可连接 CourtListener API）
const getVerificationStatus = (href: string): VerificationStatus => {
  // 可信任的法律数据库 = 已验证
  const trustedDomains = [
    'finlex.fi', 'boe.es', 'eur-lex.europa.eu', 'courtlistener.com',
    'case.law', 'scholar.google.com', 'legifrance.gouv.fr',
    'legislation.gov.uk', 'law.cornell.edu'
  ];

  for (const domain of trustedDomains) {
    if (href.includes(domain)) return 'verified';
  }

  // 有 URL 但非可信域名 = 未验证
  if (href.startsWith('http')) return 'unverified';

  return 'unverified';
};

// ==================== 用户消息附件卡片 ====================

const getAttachmentIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return FileText;
    case 'doc':
    case 'docx':
      return FileIcon;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return FileImage;
    default:
      return FileIcon;
  }
};

const AttachmentCard = ({ filename, t }: { filename: string; t: (key: any) => string }) => {
  const IconComponent = getAttachmentIcon(filename);

  return (
    <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-[#FAFAFA] rounded-lg border border-[#E5E5E5] shadow-sm">
      <div className="w-8 h-8 rounded-md bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 shadow-sm">
        <IconComponent size={16} className="text-white" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-gray-800 truncate max-w-[180px]">
          {filename}
        </span>
        <span className="text-xs text-gray-500">{t('uploaded')}</span>
      </div>
    </div>
  );
};

// ==================== 用户消息 @ 引用标签 ====================

interface MentionTagData {
  source: string;
  name: string;
}

const getMentionIcon = (source: string) => {
  const s = source.toLowerCase();
  if (s.includes('onedrive')) return Cloud;
  if (s.includes('gdrive') || s.includes('google')) return Cloud;
  if (s.includes('slack')) return FileText;
  if (s.includes('gmail') || s.includes('outlook') || s.includes('mail')) return Mail;
  if (s.includes('local')) return HardDrive;
  if (s.includes('case')) return FolderOpen;
  return FileIcon;
};

const getMentionAccent = (source: string): string => {
  const s = source.toLowerCase();
  if (s.includes('onedrive')) return 'bg-blue-500/20 border-blue-400/30';
  if (s.includes('gdrive') || s.includes('google')) return 'bg-emerald-500/20 border-emerald-400/30';
  if (s.includes('slack')) return 'bg-fuchsia-500/20 border-fuchsia-400/30';
  if (s.includes('gmail')) return 'bg-red-500/20 border-red-400/30';
  if (s.includes('outlook')) return 'bg-sky-500/20 border-sky-400/30';
  if (s.includes('case')) return 'bg-amber-500/20 border-amber-400/30';
  return 'bg-white/10 border-white/20';
};

const MentionTag = ({ source, name }: MentionTagData) => {
  const IconComponent = getMentionIcon(source);
  const accentClass = getMentionAccent(source);

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border
                  text-xs font-medium text-white/90 transition-all duration-200
                  hover:bg-white/20 ${accentClass}`}
      title={`${source}/${name}`}
    >
      <IconComponent size={12} className="text-white/70 flex-shrink-0" />
      <span className="truncate max-w-[180px]">{name}</span>
    </div>
  );
};

// 解析消息内容中的附件标记和 @ 引用标签
// 支持多个附件格式：[📎 file1.pdf, 📎 file2.pdf, ...]
// 支持 @ 引用格式：@[source/name]
const parseUserMessageContent = (content: string): { attachmentNames: string[]; mentionTags: MentionTagData[]; textContent: string } => {
  // 1. 提取 @[source/name] 引用标签
  const mentionRegex = /@\[([^\]]+)\]/g;
  const mentionTags: MentionTagData[] = [];
  let mentionMatch;
  while ((mentionMatch = mentionRegex.exec(content)) !== null) {
    const inner = mentionMatch[1];
    const slashIdx = inner.indexOf('/');
    if (slashIdx > 0) {
      mentionTags.push({
        source: inner.substring(0, slashIdx),
        name: inner.substring(slashIdx + 1),
      });
    } else {
      mentionTags.push({ source: '', name: inner });
    }
  }
  // 移除 @[...] 引用文本
  const contentWithoutMentions = content.replace(/@\[([^\]]+)\]/g, '').trim();

  // 2. 匹配格式：[📎 file1, 📎 file2, ...] 或 [📎 file]
  const attachmentRegex = /^\[([📎][^\]]+)\]\s*/;
  const match = contentWithoutMentions.match(attachmentRegex);

  if (match) {
    const bracketContent = match[1];
    const fileList = bracketContent.split(/,\s*/).map(f => f.replace(/^📎\s*/, '').trim()).filter(f => f.length > 0);

    return {
      attachmentNames: fileList,
      mentionTags,
      textContent: contentWithoutMentions.replace(attachmentRegex, '').trim()
    };
  }

  return { attachmentNames: [], mentionTags, textContent: contentWithoutMentions };
};

// ==================== 图片组件 ====================

const ImageComponent = ({ src, alt, t }: { src?: string; alt?: string; t: (key: any) => string }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (!src) return null;

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500">
        <ImageIcon className="w-5 h-5" />
        <span className="text-sm">{t('imageLoadFailed')}: {alt || src}</span>
      </div>
    );
  }

  return (
    <div className="relative my-2">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}
      <img
        src={src}
        alt={alt || 'Image'}
        className={`max-w-full h-auto rounded-lg shadow-sm border border-gray-200 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
      {alt && !loading && (
        <p className="text-xs text-gray-500 mt-1 text-center">{alt}</p>
      )}
    </div>
  );
};;

// ==================== 增强版法律引用芯片 ====================

const LegalCitationChip = ({ href, children }: { href?: string; children: React.ReactNode }) => {
  // Filter out search/index pages - only render citation chips for actual content pages
  if (href && !isContentPageUrl(href)) {
    // For search/index pages, render as plain text (not a styled citation)
    // The link text is still clickable but won't look like a formal citation
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {children}
      </a>
    );
  }

  // 安全地提取域名
  let domain = '';
  try {
    if (href) {
      domain = new URL(href).hostname.replace('www.', '');
    }
  } catch {
    domain = href || '';
  }

  // 获取children的文本内容
  const childText = typeof children === 'string' ? children :
    (Array.isArray(children) && typeof children[0] === 'string') ? children[0] : '';

  // 判断是否是纯URL链接
  const isRawUrl = childText === href || childText.startsWith('http');

  // 检测引用类型和验证状态
  const citationType = detectCitationType(href || '', childText);
  const verificationStatus = getVerificationStatus(href || '');

  const style = CITATION_STYLES[citationType];
  const verification = VERIFICATION_STYLES[verificationStatus];

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 mx-0.5
                 rounded-lg border text-sm font-medium
                 transition-all duration-200
                 no-underline hover:shadow-sm
                 group cursor-pointer
                 ${style.bgClass} ${style.textClass} ${style.borderClass} ${style.hoverClass}
                 ${verification.borderStyle}`}
      title={`${verificationStatus === 'verified' ? '✓ Verified' : '⚠ Unverified'} - ${href}`}
    >
      {/* 验证状态指示点 */}
      <span className={`w-1.5 h-1.5 rounded-full ${verification.dotClass} flex-shrink-0`} />

      {/* 类型图标 */}
      <style.Icon className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 flex-shrink-0" />

      {/* 引用文本 */}
      <span className="truncate max-w-[250px]">
        {isRawUrl ? domain : children}
      </span>
    </a>
  );
};

// ==================== Pandoc 风格引用芯片 [@key] ====================

/**
 * Detect citation type from the citation key
 * Analyzes keywords in the key to determine source type
 */
const detectCitationTypeFromKey = (key: string): CitationType => {
  const lowerKey = key.toLowerCase();

  // Court cases - tribunals, courts, judicial bodies
  if (/\b(corte|court|tribunal|jep|kko|hao|audiencia|supreme|constitutional|appeal|sentencia|auto|c-\d|t-\d)\b/.test(lowerKey)) {
    return 'case';
  }

  // Statutes/Laws - legislation, codes, acts
  if (/\b(ley|law|act|codigo|code|laki|statute|constitution|constitucion)\b/.test(lowerKey)) {
    return 'statute';
  }

  // Regulations - decrees, administrative rules
  if (/\b(decreto|decree|regulation|asetus|reglamento|ordenanza|directive|resolution)\b/.test(lowerKey)) {
    return 'regulation';
  }

  // Secondary sources - reports, government documents, policy
  if (/\b(report|informe|gobierno|government|policy|cev|comision|commission|manual|guide|handbook)\b/.test(lowerKey)) {
    return 'secondary';
  }

  return 'generic';
};

const PandocCitationChip = ({ citationKey, url, onClick }: { citationKey: string; url?: string; onClick?: () => void }) => {
  // 从 key 中提取简短显示名称（取前两部分）
  const displayName = citationKey.split('-').slice(0, 2).join('-') || citationKey;

  // 检测引用类型并获取样式
  const citationType = detectCitationTypeFromKey(citationKey);
  const style = CITATION_STYLES[citationType];

  const handleClick = (e: React.MouseEvent) => {
    // 如果有 URL，直接打开外部链接
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    // 同时也执行原来的 onClick（滚动到参考文献）
    onClick?.();
  };

  // 如果有 URL，使用 <a> 标签以获得更好的语义和可访问性
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5
                   ${style.bgClass} ${style.textClass} ${style.borderClass} ${style.hoverClass}
                   border text-xs font-semibold rounded
                   transition-all duration-200
                   cursor-pointer align-text-top no-underline
                   shadow-sm hover:shadow`}
        title={`${citationKey} - 点击打开原始来源`}
      >
        <style.Icon className="w-3 h-3" />
        <span>{displayName}</span>
      </a>
    );
  }

  // 如果没有 URL，保留原来的 button 行为
  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5
                 ${style.bgClass} ${style.textClass} ${style.borderClass} ${style.hoverClass}
                 border text-xs font-semibold rounded
                 transition-all duration-200
                 cursor-pointer align-text-top
                 shadow-sm hover:shadow`}
      title={`Citation: ${citationKey} - Click to view reference`}
    >
      <style.Icon className="w-3 h-3" />
      <span>{displayName}</span>
    </button>
  );
};

// ==================== 知识库引用样式组件 (使用新的 CitationTag) ====================

// ==================== IRAC 部分样式组件 ====================

const IRACSection = ({ type, children, defaultExpanded = true }: {
  type: 'issue' | 'rule' | 'application' | 'conclusion' | 'references';
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const sectionStyles = {
    issue: {
      borderColor: 'border-l-gray-500',
      bgColor: 'bg-gray-50/50',
      title: '问题 Issue',
      icon: AlertCircle,
    },
    rule: {
      borderColor: 'border-l-blue-500',
      bgColor: 'bg-blue-50/50',
      title: '法律规则 Rule',
      icon: BookOpen,
    },
    application: {
      borderColor: 'border-l-amber-500',
      bgColor: 'bg-amber-50/30',
      title: '分析 Application',
      icon: Scale,
    },
    conclusion: {
      borderColor: 'border-l-green-500',
      bgColor: 'bg-green-50/50',
      title: '结论 Conclusion',
      icon: CheckCircle2,
    },
    references: {
      borderColor: 'border-l-gray-400',
      bgColor: 'bg-gray-50',
      title: '参考来源 References',
      icon: Hash,
    },
  };

  const style = sectionStyles[type];
  const IconComponent = style.icon;
  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <div className={`border-l-4 ${style.borderColor} ${style.bgColor} rounded-r-lg my-4 overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-black/5 transition-colors"
      >
        <ChevronIcon className="w-4 h-4 text-gray-500" />
        <IconComponent className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-semibold text-gray-700">{style.title}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 prose prose-sm max-w-none">
          {children}
        </div>
      )}
    </div>
  );
};

// ==================== 解析 Pandoc 风格引用 [@key] ====================

const parsePandocCitations = (content: string): React.ReactNode[] => {
  // 匹配 [@key] 或 [@key1; @key2] 格式
  const citationRegex = /\[@([a-zA-Z0-9_-]+(?:;\s*@[a-zA-Z0-9_-]+)*)\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = citationRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    // 解析可能的多个引用 (如 @key1; @key2)
    const keysStr = match[1];
    const keys = keysStr.split(/;\s*@/).map((k, i) => i === 0 ? k : k);

    keys.forEach((key, i) => {
      if (i > 0) {
        parts.push(', ');
      }
      parts.push(
        <PandocCitationChip
          key={`pandoc-cite-${match!.index}-${i}`}
          citationKey={key}
          onClick={() => {
            // 滚动到参考文献部分
            const refSection = document.getElementById('references-section');
            if (refSection) {
              refSection.scrollIntoView({ behavior: 'smooth' });
              // 尝试高亮对应的引用
              const targetRef = document.querySelector(`[data-citation-key="${key}"]`);
              if (targetRef) {
                targetRef.classList.add('bg-yellow-100');
                setTimeout(() => targetRef.classList.remove('bg-yellow-100'), 2000);
              }
            }
          }}
        />
      );
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [content];
};

// ==================== 解析知识库引用 @用户文档X ====================

// 引用数据类型（来自SSE）
interface CitationData {
  sourceId: string;
  sourceName: string;
  text: string;
  pageNumber?: number;
  chunkIndex?: number;
}

const parseKnowledgeCitations = (
  content: string,
  onViewFullDocument?: (source: CitationSource) => void,
  citations?: CitationData[],  // 从SSE获取的预加载引用数据
  usedIndices?: Set<number>    // 跟踪已使用的引用索引（跨行共享）
): React.ReactNode[] => {
  // 匹配多种格式：
  // 1. [@文档名称] 或 [@文档名称-年份] - LLM生成的格式
  // 2. @《文档名称》 - 新简洁格式
  // 3. @[文档名称](sourceId) - markdown风格格式
  // 4. @用户文档N [文档名称|sourceId (页码信息)] - 带sourceId的旧格式
  // 5. @用户文档N - 简单格式
  // 6. 《文档名称》- 直接书名号格式（LLM常用）
  // LLM格式正则: [@name] 或 [@name-year]
  const llmFormatRegex = /\[@([^\]]+)\]/g;
  // 书名号格式正则 (带@): @《name》
  const bookFormatRegex = /@《([^》]+)》/g;
  // 书名号格式正则 (不带@): 《name》
  const plainBookFormatRegex = /《([^》]+)》/g;
  // markdown格式正则: @[name](id)
  const markdownFormatRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  // 旧格式正则: @用户文档N [...]
  const oldFormatRegex = /@用户文档(\d+)(?:\s*\[([^\]]+)\])?/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // 合并匹配所有格式
  const allMatches: Array<{
    index: number;
    fullMatch: string;
    docIndex?: number;
    docName?: string;
    sourceId?: string;
  }> = [];

  // 匹配LLM格式: [@文档名] 或 [@文档名-年份]
  let match: RegExpExecArray | null;
  while ((match = llmFormatRegex.exec(content)) !== null) {
    // 提取文档名（去除可能的年份后缀如 -2016）
    const rawName = match[1].trim();
    const docName = rawName.replace(/-\d{4}$/, ''); // 去除年份后缀
    allMatches.push({
      index: match.index,
      fullMatch: match[0],
      docName: docName,
    });
  }

  // 匹配书名号格式: @《文档名》
  while ((match = bookFormatRegex.exec(content)) !== null) {
    if (!allMatches.some(m => m.index === match!.index)) {
      allMatches.push({
        index: match.index,
        fullMatch: match[0],
        docName: match[1].trim(),
      });
    }
  }

  // 匹配书名号格式 (不带@): 《文档名》
  while ((match = plainBookFormatRegex.exec(content)) !== null) {
    // 确保不是已被带@格式匹配的位置（@《》会在位置-1处有@）
    const hasAtPrefix = match.index > 0 && content[match.index - 1] === '@';
    if (!hasAtPrefix && !allMatches.some(m => m.index === match!.index)) {
      allMatches.push({
        index: match.index,
        fullMatch: match[0],
        docName: match[1].trim(),
      });
    }
  }

  // 匹配markdown格式: @[name](id)
  while ((match = markdownFormatRegex.exec(content)) !== null) {
    if (!allMatches.some(m => m.index === match!.index)) {
      allMatches.push({
        index: match.index,
        fullMatch: match[0],
        docName: match[1].trim(),
        sourceId: match[2].trim(),
      });
    }
  }

  // 匹配旧格式: @用户文档N [...]
  while ((match = oldFormatRegex.exec(content)) !== null) {
    const docIndex = parseInt(match[1], 10);
    let docName: string | undefined;
    let sourceId: string | undefined;

    if (match[2]) {
      const bracketContent = match[2];
      const pipeIndex = bracketContent.indexOf('|');
      if (pipeIndex !== -1) {
        docName = bracketContent.substring(0, pipeIndex).trim();
        const afterPipe = bracketContent.substring(pipeIndex + 1);
        sourceId = afterPipe.replace(/\s*\(第?\d+页?\)?$/, '').trim();
      } else {
        docName = bracketContent.replace(/\s*\(第?\d+页?\)?$/, '').trim();
      }
    }

    // 避免重复添加已被其他格式匹配的位置
    if (!allMatches.some(m => m.index === match!.index)) {
      allMatches.push({
        index: match.index,
        fullMatch: match[0],
        docIndex,
        docName,
        sourceId,
      });
    }
  }

  // 按位置排序
  allMatches.sort((a, b) => a.index - b.index);

  // 使用传入的集合或创建新的（跨行共享需要传入同一个集合）
  const usedCitationIndices = usedIndices || new Set<number>();

  // 辅助函数：规范化文档名称用于匹配
  const normalizeName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/\.[^.]+$/, '') // 去除扩展名 (.pdf, .docx 等)
      .replace(/中华人民共和国/g, '') // 去除常见前缀
      .replace(/[-_\s]/g, '') // 去除分隔符和空格
      .replace(/_\d{4}$/g, '') // 去除年份后缀 (_2016)
      .replace(/-\d{4}$/g, '') // 去除年份后缀 (-2016)
      .replace(/\d{4}$/g, ''); // 去除末尾的年份
  };

  // 辅助函数：查找匹配的预加载引用（每次返回不同的chunk）
  const findMatchingCitation = (docName?: string, sourceId?: string): CitationData | undefined => {
    if (!citations || citations.length === 0) {
      return undefined;
    }

    // 筛选出匹配的引用（可能有多个chunk）
    const matchingCitations: { citation: CitationData; index: number; score: number }[] = [];

    citations.forEach((c, idx) => {
      // 优先通过 sourceId 精确匹配 (最高优先级)
      if (sourceId && c.sourceId === sourceId) {
        matchingCitations.push({ citation: c, index: idx, score: 100 });
        return;
      }

      // 通过名称模糊匹配
      if (docName) {
        const normalizedDocName = normalizeName(docName);
        const normalizedSourceName = normalizeName(c.sourceName);

        // 精确匹配（分数最高）
        if (normalizedDocName === normalizedSourceName) {
          matchingCitations.push({ citation: c, index: idx, score: 90 });
          return;
        }

        // 双向包含匹配
        if (normalizedSourceName.includes(normalizedDocName)) {
          // 目标名称是源名称的子串（更高分数）
          const matchRatio = normalizedDocName.length / normalizedSourceName.length;
          matchingCitations.push({ citation: c, index: idx, score: 70 + matchRatio * 20 });
          return;
        }

        if (normalizedDocName.includes(normalizedSourceName)) {
          // 源名称是目标名称的子串
          const matchRatio = normalizedSourceName.length / normalizedDocName.length;
          matchingCitations.push({ citation: c, index: idx, score: 60 + matchRatio * 20 });
          return;
        }

        // 提取核心关键词匹配（如 "国防交通法"）
        const coreDocName = normalizedDocName.replace(/法律|条例|规定|办法|实施细则/g, '');
        const coreSourceName = normalizedSourceName.replace(/法律|条例|规定|办法|实施细则/g, '');
        if (coreDocName.length > 2 && coreSourceName.length > 2) {
          if (coreSourceName.includes(coreDocName) || coreDocName.includes(coreSourceName)) {
            matchingCitations.push({ citation: c, index: idx, score: 50 });
            return;
          }
        }
      }
    });

    // 按分数排序，优先返回分数最高的匹配
    matchingCitations.sort((a, b) => b.score - a.score);

    // 从匹配的引用中找一个还没使用过的
    for (const { citation, index } of matchingCitations) {
      if (!usedCitationIndices.has(index)) {
        usedCitationIndices.add(index);
        return citation;
      }
    }

    // 如果所有都用过了，返回分数最高的匹配的（重复使用）
    if (matchingCitations.length > 0) {
      return matchingCitations[0].citation;
    }

    // 最后回退：如果有任何 citations 但没有匹配，返回第一个未使用的
    // 这确保至少能显示一些内容而不是"文档已删除"
    if (docName && citations.length > 0) {
      for (let i = 0; i < citations.length; i++) {
        if (!usedCitationIndices.has(i)) {
          usedCitationIndices.add(i);
          console.warn(`[Citation] 回退匹配: "${docName}" -> "${citations[i].sourceName}"`);
          return citations[i];
        }
      }
    }

    return undefined;
  };

  // 构建结果
  for (const m of allMatches) {
    if (m.index > lastIndex) {
      parts.push(content.slice(lastIndex, m.index));
    }

    // 查找匹配的预加载引用
    const matchedCitation = findMatchingCitation(m.docName, m.sourceId);
    const previewData: CitationSource | undefined = matchedCitation ? {
      sourceId: matchedCitation.sourceId,
      sourceName: matchedCitation.sourceName,
      chunkIndex: matchedCitation.chunkIndex,
      pageNumber: matchedCitation.pageNumber,
      previewText: matchedCitation.text,
    } : undefined;

    parts.push(
      <CitationTag
        key={`citation-${m.index}`}
        docIndex={m.docIndex || 1}
        docName={m.docName}
        sourceId={m.sourceId || matchedCitation?.sourceId}
        previewData={previewData}
        onViewFullDocument={onViewFullDocument}
      />
    );

    lastIndex = m.index + m.fullMatch.length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [content];
};


// ==================== 将纯文本 URL 转换为 Markdown 链接 ====================

/**
 * 将纯文本中的 URL 转换为 Markdown 链接格式
 * 处理两种情况：
 * 1. "标题 - https://example.com" -> "[标题](https://example.com)"
 * 2. 独立的 "https://example.com" -> "[example.com](https://example.com)"
 */
const convertPlainUrlsToMarkdownLinks = (content: string): string => {
  // URL 正则表达式
  const urlRegex = /https?:\/\/[^\s\])<>]+/g;

  // 先处理已经是 Markdown 链接格式的内容，标记它们
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s\])<>]+)\)/g;
  const existingLinks = new Set<string>();
  let match;

  while ((match = markdownLinkRegex.exec(content)) !== null) {
    existingLinks.add(match[2]); // 记录已经是链接格式的 URL
  }

  // 处理 "标题 - URL" 格式
  // 匹配模式: 任意文本 - URL (URL 前有 " - ")
  const titleUrlPattern = /([^\n[\]]+?)\s+-\s+(https?:\/\/[^\s\])<>]+)/g;

  let result = content;

  // 替换 "标题 - URL" 为 Markdown 链接
  result = result.replace(titleUrlPattern, (fullMatch, title, url) => {
    // 如果这个 URL 已经在 Markdown 链接中，跳过
    if (existingLinks.has(url)) {
      return fullMatch;
    }
    // 清理标题中的多余空格
    const cleanTitle = title.trim();
    return `[${cleanTitle}](${url})`;
  });

  // 处理独立的 URL（不在 Markdown 链接中的）
  // 需要重新检查，因为上一步可能已经转换了一些
  const updatedMarkdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s\])<>]+)\)/g;
  const processedUrls = new Set<string>();

  while ((match = updatedMarkdownLinkRegex.exec(result)) !== null) {
    processedUrls.add(match[2]);
  }

  // 查找并替换剩余的独立 URL
  result = result.replace(urlRegex, (url) => {
    // 如果已经处理过，跳过
    if (processedUrls.has(url)) {
      return url;
    }

    // 检查这个 URL 是否在括号内（即已经是 Markdown 链接的一部分）
    const urlIndex = result.indexOf(url);
    if (urlIndex > 0) {
      // 检查前一个字符是否是 '('
      const beforeChar = result.charAt(urlIndex - 1);
      if (beforeChar === '(') {
        return url;
      }
    }

    try {
      // 提取域名作为显示文本
      const domain = new URL(url).hostname.replace('www.', '');
      return `[${domain}](${url})`;
    } catch {
      return `[链接](${url})`;
    }
  });

  return result;
};

// ==================== 将 Pandoc 引用 [@key] 转换为 HTML cite 标签 ====================

/**
 * 从内容中提取引用 key 到 URL 的映射
 * 搜索类似 "- [title](url)" 或 "[title](url)" 格式的参考链接
 */
const extractCitationUrlMap = (content: string): Map<string, string> => {
  const urlMap = new Map<string, string>();

  // 匹配 Markdown 链接格式: [text](url)
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    const linkText = match[1].toLowerCase();
    const url = match[2];

    // 1. 为链接文本中的关键词创建映射
    // 例如 "Tieliikennelaki 729/2018" 应该匹配 "finlex-tieliikennelaki"
    const keywords = linkText.split(/[\s\-_/]+/).filter(w => w.length > 3);

    for (const keyword of keywords) {
      if (!urlMap.has(keyword)) {
        urlMap.set(keyword, url);
      }
    }

    // 2. 同时保存完整的链接文本作为 key
    urlMap.set(linkText.replace(/\s+/g, '-'), url);

    // 3. 基于 URL 域名创建映射（关键改进！）
    // 例如 finlex.fi -> 'finlex', boe.es -> 'boe'
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');
      // 提取域名的主要部分（如 finlex.fi -> finlex）
      const domainParts = hostname.split('.');
      if (domainParts.length >= 2) {
        const mainDomain = domainParts[0]; // 如 'finlex'
        if (mainDomain.length > 2 && !urlMap.has(mainDomain)) {
          urlMap.set(mainDomain, url);
        }
      }
    } catch {
      // 忽略 URL 解析错误
    }
  }

  return urlMap;
};

/**
 * 根据 citation key 从 URL 映射中查找匹配的 URL
 */
const findUrlForCitationKey = (key: string, urlMap: Map<string, string>): string | undefined => {
  // 将 key 转换为小写进行匹配
  const lowerKey = key.toLowerCase();

  // 直接匹配
  if (urlMap.has(lowerKey)) {
    return urlMap.get(lowerKey);
  }

  // 分割 key 中的部分进行模糊匹配
  const keyParts = lowerKey.split('-').filter(p => p.length > 2);

  // 使用 Array.from() 避免 ES2015 迭代器兼容性问题
  const entries = Array.from(urlMap.entries());
  for (const [mapKey, url] of entries) {
    // 检查 key 的主要部分是否与 mapKey 匹配
    for (const part of keyParts) {
      if (mapKey.includes(part) || part.includes(mapKey)) {
        return url;
      }
    }
  }

  return undefined;
};

/**
 * 将 Pandoc 风格的 [@key] 引用转换为 HTML <cite> 标签
 * 支持单个引用 [@key] 和多个引用 [@key1; @key2] 格式
 * 这样 ReactMarkdown + rehype-raw 就能正确处理
 * 同时提取参考文献中的 URL 并添加到 data-url 属性
 */
const convertPandocCitationsToHtml = (content: string): string => {
  // 先提取所有的 URL 映射
  const urlMap = extractCitationUrlMap(content);

  // 匹配 [@...] 格式（包括单个和多个引用）
  // 例如: [@boe-trafico-2015] 或 [@boe-trafico-2015; @dgt-portal]
  const citationRegex = /\[@([^\]]+)\]/g;

  return content.replace(citationRegex, (match, innerContent) => {
    // 检查是否包含多个引用（用分号分隔）
    if (innerContent.includes(';')) {
      // 多个引用: [@key1; @key2] -> <cite>key1</cite>, <cite>key2</cite>
      const keys = innerContent.split(/;\s*@?/).map((k: string) => k.replace(/^@/, '').trim());
      return keys
        .filter((k: string) => k.length > 0)
        .map((key: string) => {
          const url = findUrlForCitationKey(key, urlMap);
          return url
            ? `<cite data-key="${key}" data-url="${url}"></cite>`
            : `<cite data-key="${key}"></cite>`;
        })
        .join(', ');
    } else {
      // 单个引用: [@key] -> <cite>key</cite>
      const key = innerContent.trim();
      const url = findUrlForCitationKey(key, urlMap);
      return url
        ? `<cite data-key="${key}" data-url="${url}"></cite>`
        : `<cite data-key="${key}"></cite>`;
    }
  });
};


// ==================== 检测 IRAC 部分 ====================

const detectIRACSection = (text: string): 'issue' | 'rule' | 'application' | 'conclusion' | 'references' | null => {
  if (/^#+\s*(问题|issue)/i.test(text)) return 'issue';
  if (/^#+\s*(规则|rule|法律规则|legal\s*rule)/i.test(text)) return 'rule';
  if (/^#+\s*(分析|analysis|application|适用)/i.test(text)) return 'application';
  if (/^#+\s*(结论|conclusion|结果)/i.test(text)) return 'conclusion';
  if (/^#+\s*(参考|reference|来源|source|引用)/i.test(text)) return 'references';

  return null;
};

// ==================== 处理内容渲染 ====================

const ContentWithCitations = ({
  content,
  onViewFullDocument,
  citations,
  t
}: {
  content: string;
  onViewFullDocument?: (source: CitationSource) => void;
  citations?: CitationData[];
  t: (key: any) => string;
}) => {
  // 预处理内容：
  // 1. 将纯文本 URL 转换为 Markdown 链接格式
  // 2. 将 Pandoc 引用 [@key] 转换为 HTML cite 标签（这样 ReactMarkdown 能正确处理）
  const urlProcessedContent = convertPlainUrlsToMarkdownLinks(content);
  const processedContent = convertPandocCitationsToHtml(urlProcessedContent);

  // 自定义 Markdown 组件 (react-markdown v9 compatible)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markdownComponents: any = {
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
      <LegalCitationChip href={href}>{children}</LegalCitationChip>,
    // Mermaid 图表渲染：当检测到 ```mermaid 代码块时，渲染为交互式 SVG 图表
    code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
      const text = String(children).replace(/\n$/, '');
      if (className === 'language-mermaid') {
        return <MermaidRenderer chart={text} />;
      }
      // ECharts 图表渲染：当检测到 ```echarts 代码块时，渲染为交互式 Canvas 图表
      if (className === 'language-echarts') {
        return <EChartsRenderer optionString={text} />;
      }
      // Phase 3: CSV/Sheet 拟物化表格
      if (className === 'language-csv' || className === 'language-sheet') {
        return <SpreadsheetBlock content={text} />;
      }
      // Phase 3: 法律文书 A4 拟物纸张
      if (className === 'language-document') {
        return <A4PaperBlock content={text} />;
      }
      // JsonTheTrinity: Word 红线审查
      if (className === 'language-word-json') {
        try {
          const doc: WordDocumentJson = JSON.parse(text);
          return <JsonRedlineRenderer document={doc} onExport={() => window.alert('此功能将调用后端 /api/doc/generate-advanced-document 接口。\n需要在 Node.js / C# 中实现对应的路由。')} />;
        } catch { return <code className={className}>{children}</code>; }
      }
      // JsonTheTrinity: Excel 高阶表格
      if (className === 'language-excel-json') {
        try {
          const doc: ExcelDocumentJson = JSON.parse(text);
          return <JsonExcelRenderer document={doc} onExport={() => window.alert('此功能将调用后端 /api/doc/create-excel 接口。')} />;
        } catch { return <code className={className}>{children}</code>; }
      }
      // JsonTheTrinity: PPT 轮播幻灯片
      if (className === 'language-ppt-json') {
        try {
          const doc: PptDocumentJson = JSON.parse(text);
          return <JsonPptRenderer document={doc} onExport={() => window.alert('此功能将调用后端 /api/doc/generate-presentation 接口。')} />;
        } catch { return <code className={className}>{children}</code>; }
      }
      // Phase 5: 富交互表格 (AG Grid)
      if (className === 'language-data-grid') {
        return <DataGridRenderer optionString={text} />;
      }
      // Phase 5: 金融 K 线蜡烛图 (TradingView Lightweight Charts)
      if (className === 'language-kline') {
        return <KLineRenderer optionString={text} />;
      }
      // Phase 5: 高阶关系图谱 (AntV G6)
      if (className === 'language-topology') {
        return <TopologyRenderer optionString={text} />;
      }
      // Phase 5: 可拖拽流程引擎 (React Flow)
      if (className === 'language-workflow') {
        return <WorkflowRenderer optionString={text} />;
      }
      // Phase 5: 精美时间轴 (零依赖自建)
      if (className === 'language-timeline') {
        return <TimelineRenderer optionString={text} />;
      }
      return <code className={className}>{children}</code>;
    },
    // 渲染 Pandoc 引用（从 <cite data-key="..." data-url="..."> 转换而来）
    cite: ({ 'data-key': dataKey, 'data-url': dataUrl }: { 'data-key'?: string; 'data-url'?: string }) => {
      if (!dataKey) return null;
      return (
        <PandocCitationChip
          citationKey={dataKey}
          url={dataUrl}
          onClick={() => {
            // 如果没有 URL，仍然滚动到参考文献部分
            if (!dataUrl) {
              const refSection = document.getElementById('references-section');
              if (refSection) {
                refSection.scrollIntoView({ behavior: 'smooth' });
                // 尝试高亮对应的引用
                const targetRef = document.querySelector(`[data-citation-key="${dataKey}"]`);
                if (targetRef) {
                  targetRef.classList.add('bg-yellow-100');
                  setTimeout(() => targetRef.classList.remove('bg-yellow-100'), 2000);
                }
              }
            }
          }}
        />
      );
    },
    img: ({ src, alt }: { src?: string; alt?: string }) =>
      <ImageComponent src={src} alt={alt} t={t} />,
    // 为段落添加 Pandoc 引用解析
    p: ({ children }: { children?: React.ReactNode }) => {
      if (typeof children === 'string') {
        const parsed = parsePandocCitations(children);
        if (parsed.length > 1 || parsed[0] !== children) {
          return <p className="mb-3 leading-relaxed">{parsed}</p>;
        }
      }
      return <p className="mb-3 leading-relaxed">{children}</p>;
    },
    // 表格样式
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border-collapse border border-gray-300 text-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: { children?: React.ReactNode }) => (
      <thead className="bg-gray-100">{children}</thead>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
        {children}
      </th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border border-gray-300 px-3 py-2 text-gray-600">
        {children}
      </td>
    ),
    tr: ({ children }: { children?: React.ReactNode }) => (
      <tr className="hover:bg-gray-50">{children}</tr>
    ),
  };

  // 检查是否包含知识库引用（多种格式）
  // 关键修复：只有当存在 RAG citations 数据时，才匹配 [@xxx] 和 《》 格式
  // 否则这些只是 AI 回复中的普通格式（如学术引用风格），不应渲染为知识库引用标签
  const hasCitationsData = citations && citations.length > 0;
  const hasCitations = processedContent.includes('@用户文档') ||
    (hasCitationsData && processedContent.includes('@《')) ||
    (hasCitationsData && processedContent.includes('[@')) ||  // 只有存在 citations 数据时才匹配 [@xxx] 格式
    (hasCitationsData && processedContent.includes('《')) ||  // 只有存在 citations 数据时才匹配书名号格式
    /@\[.+\]\(.+\)/.test(processedContent);
  if (!hasCitations) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw as any, rehypeKatex as any]}
        components={markdownComponents}
      >
        {processedContent}
      </ReactMarkdown>
    );
  }

  // 按段落分割，分别处理引用
  const paragraphs = processedContent.split('\n\n');

  // 创建共享的已使用引用索引集合（跨段落、跨行共享）
  const sharedUsedIndices = new Set<number>();

  return (
    <>
      {paragraphs.map((paragraph, pIndex) => {
        if (paragraph.includes('@用户文档') || (hasCitationsData && paragraph.includes('@《')) || (hasCitationsData && paragraph.includes('[@')) || (hasCitationsData && paragraph.includes('《')) || /@\[.+\]\(.+\)/.test(paragraph)) {
          const lines = paragraph.split('\n');
          return (
            <div key={pIndex} className="mb-3 leading-relaxed">
              {lines.map((line, lIndex) => {
                // Parse citations first
                const parsedParts = parseKnowledgeCitations(line, onViewFullDocument, citations, sharedUsedIndices);

                // For each part, if it's a string, render it through ReactMarkdown
                // to handle markdown links; if it's a React element (CitationTag), render directly
                return (
                  <React.Fragment key={lIndex}>
                    {parsedParts.map((part, partIndex) => {
                      if (typeof part === 'string') {
                        // Render string parts through ReactMarkdown for link rendering
                        return (
                          <ReactMarkdown
                            key={`md-${partIndex}`}
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw as any]}
                            components={{
                              // Override paragraph to render inline (no wrapper <p>)
                              p: ({ children }) => <>{children}</>,
                              a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
                                <LegalCitationChip href={href}>{children}</LegalCitationChip>,
                            }}
                          >
                            {part}
                          </ReactMarkdown>
                        );
                      }
                      // Render CitationTag components directly
                      return part;
                    })}
                    {lIndex < lines.length - 1 && <br />}
                  </React.Fragment>
                );
              })}
            </div>
          );
        }

        return (
          <ReactMarkdown
            key={pIndex}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw as any]}
            components={markdownComponents}
          >
            {paragraph}
          </ReactMarkdown>
        );
      })}
    </>
  );
};


// ==================== 简单对话检测 ===================

const isSimpleQuery = (content?: string): boolean => {
  if (!content) return false;
  const trimmed = content.trim().toLowerCase();

  // 简单问候词列表
  const simpleGreetings = [
    // 中文
    '你好', '您好', '嗨', 'hi', 'hello', 'hey',
    '早上好', '下午好', '晚上好', '早安', '晚安',
    '谢谢', '感谢', '多谢', 'thanks', 'thank you',
    '再见', 'bye', 'goodbye',
    '在吗', '在不在',
    '测试', 'test',
    // 英文
    'good morning', 'good afternoon', 'good evening', 'good night',
    'what\'s up', 'how are you', '你好吗', '怎么样',
  ];

  // 检查是否匹配简单问候
  for (const greeting of simpleGreetings) {
    if (trimmed === greeting || trimmed.startsWith(greeting + ' ') || trimmed.startsWith(greeting + '，') || trimmed.startsWith(greeting + '。')) {
      return true;
    }
  }

  // 如果内容非常短（小于10个字符）且不包含法律关键词，视为简单问题
  if (trimmed.length < 10) {
    const legalKeywords = ['法律', '法规', '合同', '纠纷', '诉讼', '权利', '义务', '赔偿', '侵权',
      'law', 'legal', 'contract', 'dispute', 'lawsuit', 'rights', 'liability'];
    const hasLegalKeyword = legalKeywords.some(kw => trimmed.includes(kw));
    if (!hasLegalKeyword) {
      return true;
    }
  }

  return false;
};

// ==================== 主消息气泡组件 ====================

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isStreaming = false, currentLanguage = 'en-US', previousUserContent }) => {
  const isUser = message.role === 'user';
  const hasContent = message.content && message.content.trim().length > 0;
  const isThinking = !isUser && !hasContent && !message.is_final;
  const { t } = useTranslation(currentLanguage);

  // DocumentDrawer 状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDocId, setDrawerDocId] = useState('');
  const [drawerDocName, setDrawerDocName] = useState('');
  const [drawerHighlight, setDrawerHighlight] = useState(0);

  // 处理查看完整文档
  const handleViewFullDocument = (source: CitationSource) => {
    setDrawerDocId(source.sourceId);
    setDrawerDocName(source.sourceName);
    setDrawerHighlight(source.chunkIndex || 0);
    setDrawerOpen(true);
  };

  // 如果是空内容且已完成，不渲染任何东西
  if (!hasContent && message.is_final) {
    return null;
  }

  return (
    <div className="py-3">
      {/* 居中容器 - 确保用户消息和AI消息对齐 */}
      <div className="max-w-2xl mx-auto px-6">
        {/* 用户消息 */}
        {isUser && hasContent && (() => {
          const { attachmentNames, mentionTags, textContent } = parseUserMessageContent(message.content);
          return (
            <div className="flex justify-end">
              <div className="bg-[#1A1A1A] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-md shadow-md border border-[#1A1A1A] hover:shadow-lg transition-shadow duration-300">
                {mentionTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {mentionTags.map((tag, idx) => (
                      <MentionTag key={`mention-${idx}`} source={tag.source} name={tag.name} />
                    ))}
                  </div>
                )}
                {attachmentNames.length > 0 && (
                  <div className="space-y-1.5">
                    {attachmentNames.map((filename, idx) => (
                      <AttachmentCard key={idx} filename={filename} t={t} />
                    ))}
                  </div>
                )}
                {textContent && (
                  <p className="whitespace-pre-wrap m-0 text-sm leading-relaxed">{textContent}</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* AI 消息 */}
        {!isUser && (
          <div className="flex justify-start">
            <div className="w-full">
              {/* 思考中动画 - 统一使用简单样式 */}
              {isThinking && (
                <AIThinkingIndicator
                  currentLanguage={currentLanguage}
                  isSimple={isSimpleQuery(previousUserContent)}
                  status={message.status}
                />
              )}

              {/* 消息内容 - 使用法律专业排版 */}
              {hasContent && (() => {
                const isError = message.content.startsWith('错误:');
                if (isError) {
                  return (
                    <div className="flex items-start gap-3 p-4 bg-red-50/50 border border-red-100 rounded-xl animate-fade-in group">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 group-hover:scale-110 transition-transform" />
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-red-700">服务异常</span>
                        <p className="text-sm text-red-600/90 leading-relaxed font-medium">
                          {message.content.replace('错误:', '').trim()}
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    className="prose prose-sm max-w-none text-sm leading-relaxed text-gray-800 
                             [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                             [&_p]:leading-[1.7] [&_li]:leading-[1.6]"
                    style={{
                      fontFamily: '"Merriweather", "Georgia", "Times New Roman", serif',
                      maxWidth: '65ch',
                    }}
                    id="references-section"
                  >
                    <ContentWithCitations
                      content={message.content}
                      onViewFullDocument={handleViewFullDocument}
                      citations={message.citations}
                      t={t}
                    />
                    {isStreaming && !message.is_final && (
                      <span className="inline-block w-0.5 h-4 bg-gray-400 animate-pulse ml-0.5" />
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* 文档抽屉查看器 */}
        <DocumentDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          documentId={drawerDocId}
          documentName={drawerDocName}
          highlightChunkIndex={drawerHighlight}
        />
      </div>
    </div>
  );
};

export default MessageBubble;